import { Injectable, Provider, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { PaymentsSessionCompaniesRepository } from '@app/domain/ports/payments-companies/payments-session-companies.repository';
import { PolipayPaymentsRepository } from '@app/domain/ports/polipay-payments.repository';
import {
  PaymentsRouteCompany,
  PaymentsSessionCompany,
  dedupePaymentsSessionCompanies,
  resolvePaymentsRouteCompany,
} from '@app/domain/models/payments-companies/payments-route-company';
import { resolvePaymentsCompanyId } from '@app/domain/models/payments-companies/payments-company-id';
import { UserRoleService } from '@app/core/auth/user-role.service';
import { TAB_MEMORIES } from '@app/core/tab-memory/tab-memory.token';

/**
 * Error SINTÉTICO de falla cerrada: la pantalla lo pasa por su resolvedor de errores
 * de Payments y pinta su copy de "sin acceso" (el mismo de un 403), sin petición.
 */
export function paymentsCompanyUnresolvedError(): HttpErrorResponse {
  return new HttpErrorResponse({ status: 403, statusText: 'Forbidden' });
}

/** Clave del aviso "varios comercios, elige uno" (es/en/pt). */
export const PAYMENTS_COMPANY_MULTIPLE_KEY = 'ecommerce.paymentsCompany.multiple';

/**
 * El `id_company` de RUTA de Payments para E-commerce. UNA fuente para links, links
 * eliminados, cargos, QR y su modal. NO mira la entidad del selector de banca.
 *
 * Catálogo: `GET /api/gestor/services/` de la cuenta propia (filas con
 * `payments_company`) y, si no aporta nada (403 —exige `CanManageGestorAccess`— o
 * error), `GET /api/v1/payments/companies/` con `resolvePaymentsCompanyId`. En memoria,
 * nunca storage. Solo se CACHEA un catálogo con comercios: uno vacío (fallo o sin
 * permiso) se vuelve a pedir en la siguiente navegación de E-commerce.
 * Lo carga `paymentsRouteCompanyGuard` (canActivate + canActivateChild del layout).
 */
@Injectable({ providedIn: 'root' })
export class PaymentsRouteCompanyService {
  private readonly sessionCompanies = inject(PaymentsSessionCompaniesRepository);
  private readonly payments = inject(PolipayPaymentsRepository);
  private readonly userRole = inject(UserRoleService);

  private cuentaCargada: string | null = null;
  private catalogo: PaymentsSessionCompany[] = [];
  private enVuelo$: Observable<void> | null = null;

  /** Carga el catálogo si hace falta. Nunca falla: un error deja el catálogo vacío. */
  ensureLoaded(): Observable<void> {
    const cuenta = this.userRole.getCurrentPolipayAccount() ?? '';
    if (this.cuentaCargada !== cuenta) {
      this.cuentaCargada = cuenta;
      this.catalogo = [];
      this.enVuelo$ = null;
    }
    // Y1 (revisión 2026-10-19): sin cuenta legible la llave sería `''` y la compartirían
    // dos usuarios de la pestaña; ese catálogo se usa pero NO se reutiliza: se relee.
    if (this.catalogo.length > 0 && cuenta !== '') return of(undefined);
    if (this.enVuelo$) return this.enVuelo$;

    const fuente$ = cuenta ? this.sessionCompanies.getOwnPaymentsCompanies(cuenta) : of([]);
    this.enVuelo$ = fuente$.pipe(
      catchError(() => of([] as PaymentsSessionCompany[])),
      switchMap((lista) => (lista.length > 0 ? of(lista) : this.desdeCompanies())),
      tap((lista) => {
        if (this.cuentaCargada === cuenta) this.catalogo = dedupePaymentsSessionCompanies(lista);
      }),
      map(() => undefined),
      finalize(() => { this.enVuelo$ = null; }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.enVuelo$;
  }

  /**
   * Olvida el catálogo (memoria de pestaña, Y1): lo llama `LogoutService.forgetTabMemory()`
   * por `TAB_MEMORIES`. Una lectura en vuelo ya no se guarda: la cuenta deja de coincidir.
   */
  clear(): void {
    this.cuentaCargada = null;
    this.catalogo = [];
    this.enVuelo$ = null;
  }

  /** Decide con el catálogo ya cargado. */
  resolveActive(): PaymentsRouteCompany {
    return resolvePaymentsRouteCompany(this.catalogo);
  }

  private desdeCompanies(): Observable<PaymentsSessionCompany[]> {
    return this.payments.listCompanies().pipe(
      map((res) => {
        const lista: PaymentsSessionCompany[] = [];
        for (const c of res?.results ?? []) {
          const routeId = resolvePaymentsCompanyId({ orgCompanyId: c.org_company_id, externalRef: c.external_ref });
          if (routeId !== null) lista.push({ routeId, name: c.company_name || null });
        }
        return lista;
      }),
      catchError(() => of([] as PaymentsSessionCompany[])),
    );
  }
}

/** Registra el catálogo como memoria de pestaña (`TAB_MEMORIES`). Va SOLO en `app.config.ts`. */
export function providePaymentsRouteCompanyTabMemory(): Provider[] {
  return [{ provide: TAB_MEMORIES, multi: true, useExisting: PaymentsRouteCompanyService }];
}
